/**
 * Owner Cashiers — list, add modal (with branch multi-select), edit,
 * reset password, deactivate.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Users, Edit, KeyRound, UserX, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { FieldRow, Input } from '@/components/ui_primitives/Field';
import { confirm } from '@/components/ui_primitives/confirm';

const createSchema = z.object({
  name: z.string().min(1).max(120),
  email: z.string().email(),
  password: z.string().min(6).max(120),
  assigned_branch_ids: z.array(z.string()).min(0),
});
const editSchema = z.object({
  name: z.string().min(1).max(120),
  assigned_branch_ids: z.array(z.string()).min(0),
});

function BranchPicker({ value, onChange, branches }) {
  const toggle = (id) => {
    const set = new Set(value || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange(Array.from(set));
  };
  if (!branches?.length) {
    return <div className="text-xs text-white/40">No branches yet — add some first.</div>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {branches.filter((b) => b.is_active !== false).map((b) => {
        const active = (value || []).includes(b.id);
        return (
          <button
            key={b.id}
            type="button"
            onClick={() => toggle(b.id)}
            data-testid={`branch-pick-${b.id}`}
            className={
              'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ' +
              (active
                ? 'bg-gold/15 text-gold border-gold/30'
                : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10')
            }
          >
            {b.name}
          </button>
        );
      })}
    </div>
  );
}

export default function OwnerCashiersPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // 'new' | {edit} | {reset}

  const cashiersQ = useQuery({
    queryKey: qk.ownerCashiers(brandId),
    queryFn: () => api.ownerCashiers(token),
    enabled: !!token && !!brandId,
  });
  const branchesQ = useQuery({
    queryKey: qk.ownerBranches(brandId),
    queryFn: () => api.ownerBranches(token),
    enabled: !!token && !!brandId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.ownerCashiers(brandId) });
  const deactivate = useMutation({
    mutationFn: (id) => api.ownerDeleteCashier(token, id),
    onSuccess: () => { toast.success('Cashier deactivated'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Action failed')),
  });
  const reactivate = useMutation({
    mutationFn: (id) => api.ownerPatchCashier(token, id, { is_active: true }),
    onSuccess: () => { toast.success('Cashier reactivated'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Action failed')),
  });

  if (cashiersQ.isLoading) return <FullSpinner />;
  if (cashiersQ.error) return <QueryError error={cashiersQ.error} onRetry={() => cashiersQ.refetch()} />;

  const items = cashiersQ.data || [];
  const branches = branchesQ.data || [];
  const branchNames = (ids) =>
    (ids || []).map((id) => branches.find((b) => b.id === id)?.name).filter(Boolean);

  return (
    <div className="space-y-6" data-testid="owner-cashiers-page">
      <PageHeader
        kicker="Cashiers"
        title="Staff & access"
        subtitle="Create cashier accounts and assign them to one or more branches."
        actions={
          <Button leftIcon={<Plus size={14} />} onClick={() => setModal({ mode: 'new' })} testID="cashier-add">
            Add cashier
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState
          icon={<Users size={20} />}
          title="No cashiers yet"
          subtitle="Add your first cashier to start accepting in-store stamp transactions."
          action={<Button leftIcon={<Plus size={14} />} onClick={() => setModal({ mode: 'new' })}>Add cashier</Button>}
          testID="cashiers-empty"
        />
      ) : (
        <SectionCard padded={false}>
          <Table testID="cashiers-table">
            <THead>
              <Tr>
                <Th>Cashier</Th>
                <Th>Email</Th>
                <Th>Branches</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {items.map((c) => (
                <Tr key={c.id}>
                  <Td>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 grid place-items-center text-gold font-semibold text-xs">
                        {(c.name ?? '?').slice(0, 1).toUpperCase()}
                      </div>
                      <div className="font-medium">{c.name}</div>
                    </div>
                  </Td>
                  <Td><span className="font-mono text-xs text-white/70">{c.email}</span></Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {branchNames(c.assigned_branch_ids).map((n) => (
                        <span key={n} className="px-2 py-0.5 rounded-md text-[11px] bg-white/5 border border-white/10 text-white/70">{n}</span>
                      ))}
                      {(!c.assigned_branch_ids || c.assigned_branch_ids.length === 0)
                        ? <span className="text-[11px] text-white/30">— unassigned —</span>
                        : null}
                    </div>
                  </Td>
                  <Td>
                    {c.is_active === false
                      ? <StatusPill variant="slate">Inactive</StatusPill>
                      : <StatusPill variant="gold">Active</StatusPill>}
                  </Td>
                  <Td align="right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="subtle" leftIcon={<Edit size={12} />} onClick={() => setModal({ mode: 'edit', cashier: c })} testID={`cashier-edit-${c.id}`}>Edit</Button>
                      <Button size="sm" variant="subtle" leftIcon={<KeyRound size={12} />} onClick={() => setModal({ mode: 'reset', cashier: c })} testID={`cashier-reset-${c.id}`}>Reset</Button>
                      {c.is_active === false
                        ? <Button size="sm" variant="secondary" leftIcon={<UserCheck size={12} />} onClick={() => reactivate.mutate(c.id)} loading={reactivate.isPending} testID={`cashier-reactivate-${c.id}`}>Reactivate</Button>
                        : <Button size="sm" variant="danger" leftIcon={<UserX size={12} />} onClick={async () => {
                            const ok = await confirm({ title: `Deactivate ${c.name}?`, message: 'They will not be able to sign in until reactivated.', confirmText: 'Deactivate', tone: 'danger' });
                            if (ok) deactivate.mutate(c.id);
                          }} testID={`cashier-deactivate-${c.id}`}>Deactivate</Button>}
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </SectionCard>
      )}

      {modal?.mode === 'new' ? (
        <NewCashierModal branches={branches} onClose={() => setModal(null)} onSaved={() => { invalidate(); setModal(null); }} />
      ) : null}
      {modal?.mode === 'edit' ? (
        <EditCashierModal cashier={modal.cashier} branches={branches} onClose={() => setModal(null)} onSaved={() => { invalidate(); setModal(null); }} />
      ) : null}
      {modal?.mode === 'reset' ? (
        <ResetPasswordModal cashier={modal.cashier} onClose={() => setModal(null)} />
      ) : null}
    </div>
  );
}

function NewCashierModal({ branches, onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const { register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue } = useForm({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', email: '', password: '', assigned_branch_ids: [] },
  });
  const assigned = watch('assigned_branch_ids');
  const onSubmit = async (vals) => {
    try {
      await api.ownerCreateCashier(token, vals);
      toast.success('Cashier created');
      onSaved();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Create failed'));
    }
  };
  return (
    <Modal open onClose={onClose} title="Add cashier" size="md"
      testID="cashier-new-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting} testID="cashier-new-save">Create</Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <FieldRow label="Full name" required error={errors.name?.message}>
          <Input {...register('name')} placeholder="e.g. Citra Wulandari" />
        </FieldRow>
        <FieldRow label="Email" required error={errors.email?.message}>
          <Input type="email" {...register('email')} placeholder="cashier@brand.com" />
        </FieldRow>
        <FieldRow label="Initial password" required error={errors.password?.message} hint="Minimum 6 characters">
          <Input type="password" {...register('password')} placeholder="••••••••" />
        </FieldRow>
        <FieldRow label="Assigned branches" hint="Cashier can only stamp at these branches">
          <BranchPicker value={assigned} onChange={(v) => setValue('assigned_branch_ids', v, { shouldDirty: true })} branches={branches} />
        </FieldRow>
      </form>
    </Modal>
  );
}

function EditCashierModal({ cashier, branches, onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const { register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue } = useForm({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: cashier.name ?? '',
      assigned_branch_ids: cashier.assigned_branch_ids ?? [],
    },
  });
  const assigned = watch('assigned_branch_ids');
  const onSubmit = async (vals) => {
    try {
      await api.ownerPatchCashier(token, cashier.id, vals);
      toast.success('Cashier updated');
      onSaved();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Update failed'));
    }
  };
  return (
    <Modal open onClose={onClose} title={`Edit ${cashier.name}`} size="md"
      testID="cashier-edit-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting} testID="cashier-edit-save">Save changes</Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <FieldRow label="Full name" required error={errors.name?.message}>
          <Input {...register('name')} />
        </FieldRow>
        <FieldRow label="Email">
          <Input value={cashier.email} disabled />
        </FieldRow>
        <FieldRow label="Assigned branches">
          <BranchPicker value={assigned} onChange={(v) => setValue('assigned_branch_ids', v, { shouldDirty: true })} branches={branches} />
        </FieldRow>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ cashier, onClose }) {
  const token = useAuthStore((s) => s.token);
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const onSubmit = async () => {
    if (pwd.length < 6) { toast.error('Min 6 chars'); return; }
    setBusy(true);
    try {
      await api.ownerResetCashierPassword(token, cashier.id, pwd);
      toast.success(`Password reset for ${cashier.name}`);
      onClose();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Reset failed'));
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Reset password — ${cashier.name}`} size="sm"
      testID="cashier-reset-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} loading={busy} testID="cashier-reset-save">Reset</Button>
        </>
      }
    >
      <FieldRow label="New password" hint="Min 6 chars">
        <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="••••••••" />
      </FieldRow>
      <p className="text-xs text-white/40 mt-3">Share the new password with the cashier through a secure channel.</p>
    </Modal>
  );
}
